/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {Auditable, AuditBy, PartialProperties} from "@intuitionrobotics/ts-common";
import type {RenderMapMetaData} from "./unit-config.js";
import type {DB_Object} from "@intuitionrobotics/firebase";
import type {ApiWithBody} from "@intuitionrobotics/thunderstorm";
import type {ActivationsInterface} from "./activation.js";

export type UnitId = string;

export function getPmEnvPath(unit: Unit) {
	return `/unitsEnv/${unit.product}/${unit.unitId}`;
}

export function getProductPmEnvPath(unit: Unit) {
	return `/unitsEnv/${unit.product}`;
}

export function getPmDefaultEnvPath() {
	return `/unitsEnv/_default`;
}

export type DeviceType = "som" | "tablet"

export const unitNotFound = "unit-not-found";

export type DataEnvItem = DB_Object & {
	audit: AuditBy
	type: UserGroupTypes
	label: string
	agentIdPrefix?: string
}

export enum UserGroupTypes {
	DEMO = "Demo Users",
	IR_INTERNAL = "IR Internal",
	USERS = "Users"
}

export type Api_CreateDataEnv = ApiWithBody<"/v2/data-env/create", PartialProperties<DataEnvItem, "_id">, DataEnvItem>
export type Api_QueryDataEnv = ApiWithBody<"/v2/data-env/query", Partial<DataEnvItem>, DataEnvItem[]>

export enum UnitEnv { // we now have a collection for this, don't update anymore pls. Just still here only for migration
	// DEV
	QA          = "qa",
	Maintenance = "maintenance",
	Support     = "support",
	Refurbished   = "temp",
	Dev         = "dev",
	Disenrolled = "disenrolled"
}

export type DeviceIdentity = BarcodeV1 & Auditable & {
	factoryId?: string
	_updated?: number
}

export type UnpairedDeviceIdentity = DeviceIdentity & {
	_unpaired?: AuditBy
};

export type PartialUnitInfo = {
	identity: BarcodeV1
	pair: DB_PairedUnit
}

export type FullUnitInfo = {
	devices: BarcodeV1[]
	pair: DB_PairedUnit
}

export type DeviceBarcodeV1 = {
	type: DeviceType
	serial: string
	btMac: string
	publicKey: string
	sha256: string
	wifiMac: string
	version: string
}

export type BarcodeV1 = DeviceBarcodeV1 & {
	factoryId?: string
	fakeDevice?: boolean
}

export type DB_UnpairedUnit = {
	_id: string;
	_unpaired?: AuditBy;
	_deleted?: AuditBy;
	deleted?: boolean
	repaired?: boolean
} & DB_PairedUnit

export type DB_DeletedUnitGsuit = {
	unpairedUnit: DB_UnpairedUnit,
	primaryAccountEmail?: string
}

export type DB_PairedUnit = Auditable & {
	sha256: string[],
	unitId: string,
	product: string,
	version?: string,
	comment?: string,
	devUnit?: boolean,
	softwareOnly?: boolean
	ttl?: number
}
export type PairedUnitAndContact = DB_PairedUnit & {
	contactIds?: string[]
}

export type Request_ListUnits = {
	product?: string
}

export type Request_ListAgents = {
	product?: string,
	includeComment?: string
}

export type Request_ListAgent = Unit & {
	includeComment?: string
}

export type Unit = UnitIdType & {
	product: string;
}


export type Response_GoogleTokenProxy = {
	token: { access_token?: string | null }
}

export type Response_ListUnits = {
	units: Unit[]
}

export type UnitAndComment = Unit & {
	comment?: string
}
export type Response_ListAgents = {
	units: UnitAndComment[]
}

export type FullUnitConfig = DB_PairedUnit & {
	identities: DeviceIdentity[]
};

export type UnitBasic = {
	unitId: string,
	somSerial: string,
	tabletSerial: string,
	pair?: boolean,
	activation?: boolean
};

export type Response_FullListUnits = FullUnitConfig[]

export type Response_FullListUnitsByEnvs = {
	dev: FullUnitConfig[],
	stg: FullUnitConfig[],
	prod: FullUnitConfig[]
}

export type Response_ListeningPath = {
	path: string
	db: string
}

export type EnvironmentNode = {
	db_name: string
	state_firebase_project: string
}

export type UnitIdType = { unitId: string }

export type UnitWithUserGroupAndSubGroup = UnitIdType & {
	userGroup: string
	subUserGroup?: string
};
export type Base_UpdateUnitEnv = UnitWithUserGroupAndSubGroup & {
	prevUserGroup?: string
}

type CreatorEmail = { creatorEmail: string }

export type ReqBody_UpdateUnitEnv = {
	units: Unit[]
	env: string
	timestamp: number
	creatorEmail: string
}

export type ReqBody_UpdateUnitMetadata = {
	unit: Unit[]
	metadata: Partial<RenderMapMetaData>
}

export type ReqBody_GetUnitMetadata = Unit

export type UnitDefinition = Unit & {
	sha256?: string
}

type UnitMetadataDef = UnitDefinition & {
	key: keyof RenderMapMetaData
};
export type ReqBody_GetUnitMetadataValue = UnitMetadataDef
export type ReqBody_DeleteUnitMetadataValue = UnitMetadataDef

export type ReqBody_UpdateUnitSuffix = { data: (Unit & { suffix: string })[] }

export type Request_Register = {
	factoryId?: string
	qrCode: BarcodeV1
}

export type Body_DeleteDevice = {
	deviceSha: string,
	_unpaired?: AuditBy;
}

export type KasperoReq_Register = Request_Register & CreatorEmail

export type BaseRequest_Pair = {
	product: string
	firebaseKey?: string
	version?: string
	unitId: string
	factoryId?: string
	devUnit?: boolean
	softwareOnly?: boolean
	comment?: string
	ttl?: number
}

export type Kaspero_Request_Pair = CreatorEmail & BaseRequest_Pair & {
	barcodes: BarcodeV1[]
}


export type BaseUnpair = Unit

export type Request_UnpairUnit = Unit & {
	creatorEmail?: string,
	userGroup?: string
}

export type Kaspero_UnpairUnit = BaseUnpair & {
	creatorEmail: string
}

export type Kaspero_UnpairUnitAll = {
	creatorEmail: string
	units: Unit[]
}

export type RequestUnpairDelete = {
	units: Unit[]
}

export type Request_UnitIdentity = {
	unitId: string
	product: string
}

export type UnitIdentity = DB_PairedUnit & {
	devices: DeviceIdentity[]
}

export type Request_DeviceIdentity = ShaParameter

export type Request_Identity = ShaParameter

export type Request_Identity_V2 = DeviceBarcodeV1

export type EncryptedData = {
	encryptedData: string[],
};

export type Response_Identity = EncryptedData

export type Response_Identity_V2 = EncryptedData | void

export type Response_Identities = {
	identities: DeviceIdentity[]
	requests: DeviceIdentity[]
	unpairedIdentities: DeviceIdentity[]
}

export type ShaParameter = {
	sha256: string
}

export type Request_EncryptJWT = {
	sha256: string
	jwt: string
}

export type BaseRequestFirebaseToken = {
	db: string
	verifyToken?: "true" | "false"
};
export type Request_FirebaseToken = ShaParameter & BaseRequestFirebaseToken

export type Request_FirebaseTokenV2 = BaseRequestFirebaseToken & {
	packageName: string
}

export type Request_FirebaseTokenProxy = Unit & BaseRequestFirebaseToken & {
	tokenizerAccount: string | undefined
}

export type Response_FirebaseToken = EncryptedData & {
	apiKey: string
}

export type Response_FirebaseTokenV2 =  string

export type Response_FirebaseTokenProxy = {
	token: string
	apiKey: string
}

export type DeviceWithSameSerialAlreadyInSystem = {
	serial: string
}

export type UnitAndContact = AgentInfo<any> & {
	contactIds?: string[]
}

export type AgentInfoDetails = {
	activation: ActivationsInterface & {comment?: string}
	paired: DB_PairedUnit
}

export type AgentInfo<K extends keyof AgentInfoDetails> = {
	detail: AgentInfoDetails[K]
	type: K
}
